import { FC, useCallback, useMemo, useState } from 'react';
import { Box, Stack, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useAppSelector } from '../../../../../../../../../hooks/redux';
import moment from 'moment-timezone';
import NumberFormat from 'react-number-format';
import { IPayment } from '../../../../../../../../../models/IPayment';
import NewPaymentFormStep from './NewPaymentFormStep';
import OtherPaymentMethods from './OtherPaymentMethods';
import validationSchema from './new-payment-schema';
import useNewPaymentOptions from './use-new-payment-options';
import NewPaymentFormOption from './NewPaymentFormOption';
import EntityDrawerContent from '../../../../../../../../entity-drawer-layout/EntityDrawerContent';
import FocusTextField from '../../../../../../../../FocusTextField';
import EntityDrawerActions from '../../../../../../../../entity-drawer-layout/EntityDrawerActions';
import PermanentEntityDrawer from 'ui-component/PermanentEntityDrawer';

enum PaymentFormStep {
    Method = 'method',
    MethodOther = 'method_other',
    MethodCoupon = 'method_coupon',
    Reason = 'reason',
    Amount = 'amount'
}

type NewPaymentFormType = {
    datetime: string;
    method: string;
    reason: Array<string>;
    amount_service: number | null;
    amount_deposit: number | null;
    amount_gratuity: number | null;
    code?: string;
};

interface NewPaymentFormProps {
    open: boolean;
    onSubmit: (id: string, data: IPayment[]) => void;
    onCancel: () => void;
}

const NewPaymentFormDrawer: FC<NewPaymentFormProps> = ({ open, onSubmit, onCancel }) => {
    const { coreMethods, coreReasons, getLabel, getIcon } = useNewPaymentOptions();
    const { selectedEvent } = useAppSelector((state) => state.calendar);

    const [step, setStep] = useState<PaymentFormStep>(PaymentFormStep.Method);

    const formInitialValues = useMemo(
        () => ({
            datetime: moment()
                .tz(selectedEvent?.location.time_zone || 'utc')
                .toString(),
            method: '',
            reason: [],
            amount_service: null,
            amount_deposit: null,
            amount_gratuity: null,
            code: ''
        }),
        [selectedEvent]
    );

    const { values, setFieldValue, setFieldTouched, touched, errors, handleChange, handleBlur, handleSubmit, resetForm } =
        useFormik<NewPaymentFormType>({
            validateOnMount: true,
            initialValues: formInitialValues,
            enableReinitialize: true,
            validateOnChange: true,
            validateOnBlur: true,
            validationSchema,
            onSubmit: (formData) => {
                const payments: IPayment[] = formData.reason.map((reason) => {
                    const amountKey: `amount_${'service' | 'deposit' | 'gratuity'}` = `amount_${
                        reason as 'service' | 'deposit' | 'gratuity'
                    }`;
                    return {
                        ...formData,
                        datetime: moment(formData.datetime).tz('UTC').toString(),
                        reason,
                        method: formData.method ? formData.method : undefined,
                        code: formData.code ? formData.code : undefined,
                        amount: formData[amountKey] ?? undefined
                    };
                });

                onSubmit(String(selectedEvent?.id), payments);
            }
        });

    const handleMethodSelect = useCallback(
        (method: string) => {
            switch (method) {
                case 'other_methods': {
                    setStep(PaymentFormStep.MethodOther);
                    break;
                }

                case 'discount_code': {
                    setFieldValue('reason', ['coupon']);
                    setStep(PaymentFormStep.MethodCoupon);
                    break;
                }

                default: {
                    setFieldValue('method', method);
                    setStep(PaymentFormStep.Reason);
                }
            }
        },
        [setFieldValue]
    );

    const handleReasonSelect = useCallback(
        (reason: string) => {
            const newVal = values.reason.includes(reason) ? [...values.reason].filter((r) => r !== reason) : [...values.reason, reason];
            setFieldValue('reason', newVal);
        },
        [setFieldValue, values.reason]
    );

    const total = useMemo(
        () => (values.amount_deposit ?? 0) + (values.amount_service ?? 0) + (values.amount_gratuity ?? 0),
        [values.amount_deposit, values.amount_gratuity, values.amount_service]
    );

    const handlePrev = useCallback(() => {
        switch (step) {
            case PaymentFormStep.MethodOther:
            case PaymentFormStep.MethodCoupon:
            case PaymentFormStep.Reason:
                resetForm();
                setStep(PaymentFormStep.Method);
                break;
            case PaymentFormStep.Amount:
                setFieldTouched('amount', false);
                setFieldValue('amount', { service: null, deposit: null, gratuity: null });
                setStep(PaymentFormStep.Reason);
                break;
            default:
                onCancel();
                break;
        }
    }, [onCancel, resetForm, setFieldTouched, setFieldValue, step]);

    return (
        <PermanentEntityDrawer open={open} title="New Payment" onClose={onCancel} onBackAction={handlePrev}>
            <EntityDrawerContent>
                <form id="new-payment-form" onSubmit={handleSubmit} noValidate autoComplete="off">
                    {step === PaymentFormStep.Method && (
                        <NewPaymentFormStep label="Select a Payment Method" handlePrev={handlePrev}>
                            <Stack spacing={2}>
                                {coreMethods.map((method) => (
                                    <NewPaymentFormOption
                                        icon={getIcon(method)}
                                        key={method}
                                        onClick={() => {
                                            handleMethodSelect(method);
                                        }}
                                    >
                                        {getLabel(method)}
                                    </NewPaymentFormOption>
                                ))}
                            </Stack>
                        </NewPaymentFormStep>
                    )}

                    {step === PaymentFormStep.MethodOther && (
                        <NewPaymentFormStep label="Select a Payment Method" handlePrev={handlePrev}>
                            <Box>
                                <OtherPaymentMethods onSelect={handleMethodSelect} />
                            </Box>
                        </NewPaymentFormStep>
                    )}

                    {step === PaymentFormStep.MethodCoupon && (
                        <NewPaymentFormStep label="Enter Coupon Code" canSubmit={!!values.code} handlePrev={handlePrev}>
                            <Box>
                                <TextField
                                    fullWidth
                                    name="code"
                                    value={values.code}
                                    label="Coupon Code"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={Boolean(touched.code && errors.code)}
                                    helperText={touched.code ? errors.code : undefined}
                                />
                            </Box>
                        </NewPaymentFormStep>
                    )}

                    {step === PaymentFormStep.Reason && (
                        <NewPaymentFormStep
                            label="Select a Payment Purpose"
                            nextBlocked={values.reason.length < 1}
                            handleNext={() => setStep(PaymentFormStep.Amount)}
                            handlePrev={handlePrev}
                        >
                            <Stack spacing={2}>
                                {coreReasons.map((reason) => (
                                    <NewPaymentFormOption
                                        key={reason}
                                        icon={getIcon(reason)}
                                        active={values.reason.includes(reason)}
                                        onClick={() => {
                                            handleReasonSelect(reason);
                                        }}
                                    >
                                        {getLabel(reason)}
                                    </NewPaymentFormOption>
                                ))}
                            </Stack>
                        </NewPaymentFormStep>
                    )}

                    {step === PaymentFormStep.Amount && (
                        <NewPaymentFormStep label="Enter a Payment Amount" canSubmit handleNext={handleSubmit} handlePrev={handlePrev}>
                            <Stack spacing={3}>
                                {[...coreReasons]
                                    .filter((reason) => values.reason.includes(reason))
                                    .map((res) => (
                                        <NumberFormat
                                            key={res}
                                            fullWidth
                                            customInput={FocusTextField}
                                            error={!!errors[`amount_${res}`] && !!touched[`amount_${res}`]}
                                            helperText={touched[`amount_${res}`] ? errors[`amount_${res}`] : undefined}
                                            id={`amount_${res}`}
                                            name={`amount_${res}`}
                                            value={values[`amount_${res}`] ?? ''}
                                            label={`${getLabel(res)} Amount`}
                                            prefix="$"
                                            decimalScale={2}
                                            fixedDecimalScale
                                            inputProps={{ step: '0.01', min: '0.01' }}
                                            onBlur={handleBlur}
                                            onValueChange={(e) => {
                                                setFieldValue(`amount_${res}`, e.floatValue);
                                            }}
                                        />
                                    ))}
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    spacing={1}
                                    pt={1.5}
                                    sx={{ borderTop: '1px solid', borderColor: 'grey.200' }}
                                >
                                    Total {getLabel(values.method)} Payments:
                                    <Box
                                        sx={{
                                            maxWidth: '150px'
                                        }}
                                    >
                                        <NumberFormat displayType="text" prefix="$" decimalScale={2} fixedDecimalScale value={total} />
                                    </Box>
                                </Stack>
                            </Stack>
                        </NewPaymentFormStep>
                    )}
                </form>
            </EntityDrawerContent>
            <EntityDrawerActions id="new-payment-form-actions" />
        </PermanentEntityDrawer>
    );
};

export default NewPaymentFormDrawer;
